import { TriangleAlert } from "lucide-react";
import { useEffect, useRef } from "react";
import { useRouteError } from "react-router";

import { HelpProvider } from "@/components/help";
import { Button, buttonClass, EmptyState, HelpLink } from "@/components/ui";

function CrashContent() {
  // The error is only logged for developers: its message and stack are never shown to the customer.
  const error = useRouteError();
  // Logged from an effect, once per error: StrictMode runs effects twice, the ref keeps the second run quiet.
  const logged = useRef<{ error: unknown } | null>(null);
  useEffect(() => {
    if (logged.current?.error === error) return;
    logged.current = { error };
    console.error("Route error", error);
  }, [error]);
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <EmptyState
        icon={<TriangleAlert className="size-5" aria-hidden />}
        title="Something went wrong"
        description="This page hit an unexpected problem. Reloading usually fixes it. If it keeps happening, our help articles may point you to a fix."
        action={
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button onClick={() => window.location.reload()}>Reload page</Button>
            {/* a plain link: the router's own state may be what broke */}
            <a href="/" className={buttonClass("secondary")}>
              Back to dashboard
            </a>
            <HelpLink helpKey="error:generic" />
          </div>
        }
      />
    </div>
  );
}

/**
 * The route-level `errorElement`: shown when a page throws while rendering.
 * `withHelp` is for routes inside an organization, where the help panel can be opened: the app
 * shell that normally provides it is what crashed, so this page brings its own.
 */
export function RouteErrorPage({ withHelp = false }: { withHelp?: boolean }) {
  return withHelp ? (
    <HelpProvider>
      <CrashContent />
    </HelpProvider>
  ) : (
    <CrashContent />
  );
}
